import { useAuth } from '@/context/AuthProvider';
import { getOrganizationId, clearOrganizationId } from '@/context/authClient';
import { ApiErrorCode } from './apiErrorCodes';
import { FeatureAccessError } from './FeatureAccessError';

export function useAuthedFetch() {
  const { getAccessTokenSilently, clearSession } = useAuth();

  return async (url: string, options: RequestInit = {}): Promise<Response> => {
    try {
      const token = await getAccessTokenSilently();
      const orgId = getOrganizationId();
      const headers: Record<string, string> = {
        ...((options.headers as Record<string, string>) ?? {}),
        Authorization: `Bearer ${token}`,
      };
      if (orgId) {
        headers['X-Organization-Id'] = orgId;
      }

      const response = await fetch(url, {
        ...options,
        headers,
      });

      if (response.status === 401) {
        clearSession();
      }

      if (response.status === 403) {
        const body = await response
          .clone()
          .json()
          .catch(() => null);
        if (body?.code === ApiErrorCode.FEATURE_NOT_AVAILABLE) {
          throw new FeatureAccessError(body.code);
        }
        clearSession();
      }

      if (response.status === 400) {
        const body = await response
          .clone()
          .json()
          .catch(() => null);
        if (body?.code === ApiErrorCode.ORGANIZATION_SELECTION_REQUIRED) {
          clearOrganizationId();
          window.location.replace('/');
          return response;
        }
      }

      return response;
    } catch (error) {
      if (
        error instanceof FeatureAccessError ||
        (error instanceof DOMException && error.name === 'AbortError')
      ) {
        throw error;
      }
      clearSession();
      throw error;
    }
  };
}
